import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { useAuthStore } from '../../store';

const KYC_COLORS = {
  approved: { bg: '#eaf4ef', color: '#2d6a4f', label: '✓ Aprobado' },
  pending:  { bg: '#fef3e2', color: '#b45309', label: '⏳ Pendiente' },
  review:   { bg: '#fef3e2', color: '#b45309', label: '👁 En revisión' },
  rejected: { bg: '#faeef4', color: '#9b2c5e', label: '✕ Rechazado' },
};

export default function AdminUsers() {
  const { impersonate } = useAuthStore();
  const [tab, setTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [kycPending, setKycPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [imgModal, setImgModal] = useState(null);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/v1/admin/users');
      setUsers(Array.isArray(res.data) ? res.data : []);
    } catch { setUsers([]); }
    finally { setLoading(false); }
  };

  const loadKycPending = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/v1/kyc/admin/pending');
      setKycPending(Array.isArray(res.data) ? res.data : []);
    } catch { setKycPending([]); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (tab === 'users') loadUsers();
    else loadKycPending();
  }, [tab]);

  const toggleActive = async (userId, currentStatus) => {
    try {
      await api.patch(`/api/v1/admin/users/${userId}`, { is_active: !currentStatus });
      setUsers(us => us.map(u => u.id === userId ? { ...u, is_active: !currentStatus } : u));
    } catch { alert('No se pudo actualizar el usuario'); }
  };

  const makeAdmin = async (userId, currentRole) => {
    const newRole = currentRole === 'admin' ? 'sender' : 'admin';
    if (!window.confirm(`¿Cambiar rol a ${newRole}?`)) return;
    try {
      await api.patch(`/api/v1/admin/users/${userId}`, { role: newRole });
      setUsers(us => us.map(u => u.id === userId ? { ...u, role: newRole } : u));
    } catch { alert('No se pudo cambiar el rol'); }
  };

  const approveKyc = async (userId) => {
    setProcessing(true);
    try {
      await api.post(`/api/v1/kyc/admin/${userId}/approve`, { notes: 'Aprobado manualmente' });
      setKycPending(p => p.filter(u => u.id !== userId));
      setSelected(null);
    } catch { alert('Error al aprobar KYC'); }
    finally { setProcessing(false); }
  };

  const rejectKyc = async (userId) => {
    if (!rejectReason.trim()) { alert('Escribe el motivo del rechazo'); return; }
    setProcessing(true);
    try {
      await api.post(`/api/v1/kyc/admin/${userId}/reject`, { reason: rejectReason });
      setKycPending(p => p.filter(u => u.id !== userId));
      setSelected(null);
      setRejectReason('');
    } catch { alert('Error al rechazar KYC'); }
    finally { setProcessing(false); }
  };

  const filtered = users.filter(u => {
    const matchSearch = !search ||
      u.email?.toLowerCase().includes(search.toLowerCase()) ||
      u.full_name?.toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === 'all' ||
      u.role?.toLowerCase() === filter ||
      u.kyc_status?.toLowerCase() === filter;
    return matchSearch && matchFilter;
  });

  return (
    <div>
      {/* Image lightbox */}
      {imgModal && (
        <>
          <div onClick={() => setImgModal(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.85)', zIndex: 2000 }} />
          <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', zIndex: 2001, maxWidth: '90vw', maxHeight: '90vh' }}>
            <img src={imgModal} alt="KYC" style={{ maxWidth: '85vw', maxHeight: '85vh', borderRadius: 12, objectFit: 'contain' }} />
            <button onClick={() => setImgModal(null)} style={{ position: 'absolute', top: -16, right: -16, width: 36, height: 36, borderRadius: '50%', background: '#fff', border: 'none', cursor: 'pointer', fontSize: 18, fontWeight: 700 }}>✕</button>
          </div>
        </>
      )}

      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontFamily: 'Georgia,serif', fontSize: 26, margin: '0 0 4px' }}>Usuarios</h1>
        <p style={{ fontSize: 13, color: '#7a7060', margin: 0 }}>
          {tab === 'users' ? `${users.length} usuarios registrados` : `${kycPending.length} verificaciones pendientes`}
        </p>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, background: '#f0ebe2', borderRadius: 10, padding: 4, marginBottom: 20, width: 'fit-content' }}>
        <button onClick={() => setTab('users')} style={{ padding: '8px 18px', borderRadius: 7, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, background: tab === 'users' ? '#fff' : 'transparent', color: tab === 'users' ? '#0f0d0b' : '#7a7060' }}>
          👥 Usuarios
        </button>
        <button onClick={() => setTab('kyc')} style={{ padding: '8px 18px', borderRadius: 7, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, background: tab === 'kyc' ? '#fff' : 'transparent', color: tab === 'kyc' ? '#0f0d0b' : '#7a7060', display: 'flex', alignItems: 'center', gap: 6 }}>
          🪪 Revisión KYC
          {kycPending.length > 0 && tab !== 'kyc' && (
            <span style={{ background: '#c94a1e', color: '#fff', fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 20 }}>{kycPending.length}</span>
          )}
        </button>
      </div>

      {/* ── USERS TAB ── */}
      {tab === 'users' && (
        <>
          <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
            <input style={{ padding: '9px 14px', border: '1px solid #e2dbd0', borderRadius: 8, fontSize: 13, outline: 'none', width: 240 }}
              placeholder="Buscar por email o nombre..."
              value={search} onChange={e => setSearch(e.target.value)} />
            {['all', 'sender', 'admin', 'pending', 'approved', 'review'].map(f => (
              <button key={f} onClick={() => setFilter(f)} style={{ padding: '8px 14px', borderRadius: 20, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, background: filter === f ? '#c94a1e' : '#f0ebe2', color: filter === f ? '#fff' : '#7a7060' }}>
                {f === 'all' ? 'Todos' : f}
              </button>
            ))}
          </div>

          <div style={{ background: '#fff', border: '1px solid #e2dbd0', borderRadius: 14, overflow: 'hidden', overflowX: 'auto' }}>
            {loading ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#7a7060' }}>Cargando...</div>
            ) : filtered.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#7a7060' }}>No hay usuarios que coincidan</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
                <thead>
                  <tr>{['Usuario', 'País', 'Rol', 'KYC', 'Estado', 'Registro', 'Acciones'].map(h => (
                    <th key={h} style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: '#7a7060', padding: '12px 16px', background: '#faf8f5', borderBottom: '1px solid #e2dbd0', textAlign: 'left', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}</tr>
                </thead>
                <tbody>
                  {filtered.map((u, i) => {
                    const kyc = KYC_COLORS[u.kyc_status] || KYC_COLORS.pending;
                    return (
                      <tr key={i} style={{ borderBottom: '1px solid #f0ebe2' }}>
                        <td style={{ padding: '13px 16px' }}>
                          <div style={{ fontWeight: 700, fontSize: 13 }}>{u.full_name || '—'}</div>
                          <div style={{ fontSize: 12, color: '#7a7060' }}>{u.email}</div>
                        </td>
                        <td style={{ padding: '13px 16px', fontSize: 13 }}>{u.country || '—'}</td>
                        <td style={{ padding: '13px 16px' }}>
                          <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: u.role === 'admin' ? '#eaf1fb' : '#f0ebe2', color: u.role === 'admin' ? '#1a4e8c' : '#7a7060' }}>
                            {u.role}
                          </span>
                        </td>
                        <td style={{ padding: '13px 16px' }}>
                          <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: kyc.bg, color: kyc.color }}>
                            {kyc.label}
                          </span>
                        </td>
                        <td style={{ padding: '13px 16px' }}>
                          <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: u.is_active ? '#eaf4ef' : '#faeef4', color: u.is_active ? '#2d6a4f' : '#9b2c5e' }}>
                            {u.is_active ? 'Activo' : 'Bloqueado'}
                          </span>
                        </td>
                        <td style={{ padding: '13px 16px', fontSize: 12, color: '#7a7060', whiteSpace: 'nowrap' }}>
                          {u.created_at ? new Date(u.created_at).toLocaleDateString('es-ES') : '—'}
                        </td>
                        <td style={{ padding: '13px 16px' }}>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button onClick={() => toggleActive(u.id, u.is_active)} style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid #e2dbd0', background: '#fff', fontSize: 11, cursor: 'pointer', fontWeight: 600, color: u.is_active ? '#9b2c5e' : '#2d6a4f', whiteSpace: 'nowrap' }}>
                              {u.is_active ? 'Bloquear' : 'Activar'}
                            </button>
                            <button onClick={() => makeAdmin(u.id, u.role)} style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid #e2dbd0', background: '#fff', fontSize: 11, cursor: 'pointer', fontWeight: 600, color: '#1a4e8c', whiteSpace: 'nowrap' }}>
                              {u.role === 'admin' ? '→ Sender' : '→ Admin'}
                            </button>
                            {(u.kyc_status === 'review' || u.kyc_status === 'pending') && (
                              <button onClick={() => { setTab('kyc'); loadKycPending(); }} style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid #b45309', background: '#fef3e2', fontSize: 11, cursor: 'pointer', fontWeight: 600, color: '#b45309', whiteSpace: 'nowrap' }}>
                                KYC →
                              </button>
                            )}
                            <button onClick={() => impersonate(u.id)} style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid #1a4e8c', background: '#eaf1fb', fontSize: 11, cursor: 'pointer', fontWeight: 600, color: '#1a4e8c', whiteSpace: 'nowrap' }}>
                              👁 Ver como
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {/* ── KYC REVIEW TAB ── */}
      {tab === 'kyc' && (
        <div style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 420px' : '1fr', gap: 18 }}>
          {/* List */}
          <div>
            {loading ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#7a7060' }}>Cargando...</div>
            ) : kycPending.length === 0 ? (
              <div style={{ background: '#fff', border: '1px solid #e2dbd0', borderRadius: 14, padding: 48, textAlign: 'center' }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
                <div style={{ fontFamily: 'Georgia,serif', fontSize: 18 }}>Sin verificaciones pendientes</div>
              </div>
            ) : kycPending.map((u, i) => (
              <div key={i}
                onClick={() => setSelected(selected?.id === u.id ? null : u)}
                style={{ background: '#fff', border: `1px solid ${selected?.id === u.id ? '#c94a1e' : '#e2dbd0'}`, borderRadius: 12, padding: '16px 20px', marginBottom: 8, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                onMouseOver={e => { if (selected?.id !== u.id) e.currentTarget.style.background = '#f9f6f1'; }}
                onMouseOut={e => { if (selected?.id !== u.id) e.currentTarget.style.background = '#fff'; }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  {/* Thumbnail previews */}
                  {u.submission?.selfie_url && (
                    <img src={u.submission.selfie_url} alt="selfie" style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover', border: '2px solid #e2dbd0', flexShrink: 0 }} />
                  )}
                  {!u.submission?.selfie_url && (
                    <div style={{ width: 44, height: 44, borderRadius: '50%', background: '#f0ebe2', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>🪪</div>
                  )}
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 2 }}>{u.full_name || '—'}</div>
                    <div style={{ fontSize: 12, color: '#7a7060' }}>{u.email}</div>
                    <div style={{ fontSize: 11, color: '#b0a090', marginTop: 2 }}>
                      {u.created_at ? new Date(u.created_at).toLocaleDateString('es-ES') : '—'} · {u.country || '—'}
                      {u.submission?.score && <span> · Score: {u.submission.score.toFixed(0)}%</span>}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: KYC_COLORS[u.kyc_status]?.bg || '#fef3e2', color: KYC_COLORS[u.kyc_status]?.color || '#b45309' }}>
                    {KYC_COLORS[u.kyc_status]?.label || u.kyc_status}
                  </span>
                  <span style={{ color: '#c94a1e', fontSize: 16 }}>→</span>
                </div>
              </div>
            ))}
          </div>

          {/* Detail panel */}
          {selected && (
            <div style={{ background: '#fff', border: '1px solid #e2dbd0', borderRadius: 14, padding: 22, height: 'fit-content', position: 'sticky', top: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
                <div style={{ fontFamily: 'Georgia,serif', fontSize: 17 }}>Revisión KYC</div>
                <button onClick={() => { setSelected(null); setRejectReason(''); }} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 18, color: '#7a7060' }}>✕</button>
              </div>

              {/* Images */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 18 }}>
                <div>
                  <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: '#7a7060', marginBottom: 6 }}>Documento</div>
                  {selected.submission?.document_url ? (
                    <img
                      src={selected.submission.document_url}
                      alt="Documento"
                      onClick={() => setImgModal(selected.submission.document_url)}
                      style={{ width: '100%', height: 120, objectFit: 'cover', borderRadius: 8, border: '1px solid #e2dbd0', cursor: 'zoom-in' }}
                    />
                  ) : (
                    <div style={{ width: '100%', height: 120, borderRadius: 8, border: '2px dashed #e2dbd0', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#b0a090', fontSize: 12 }}>
                      Sin imagen
                    </div>
                  )}
                </div>
                <div>
                  <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: '#7a7060', marginBottom: 6 }}>Selfie</div>
                  {selected.submission?.selfie_url ? (
                    <img
                      src={selected.submission.selfie_url}
                      alt="Selfie"
                      onClick={() => setImgModal(selected.submission.selfie_url)}
                      style={{ width: '100%', height: 120, objectFit: 'cover', borderRadius: 8, border: '1px solid #e2dbd0', cursor: 'zoom-in' }}
                    />
                  ) : (
                    <div style={{ width: '100%', height: 120, borderRadius: 8, border: '2px dashed #e2dbd0', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#b0a090', fontSize: 12 }}>
                      Sin imagen
                    </div>
                  )}
                </div>
              </div>
              <div style={{ fontSize: 11, color: '#7a7060', textAlign: 'center', marginBottom: 16 }}>
                Clic en las imágenes para ampliar
              </div>

              {/* Scores */}
              {selected.submission?.score && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 16 }}>
                  {[
                    { label: 'Score', value: `${selected.submission.score.toFixed(0)}%` },
                    { label: 'Face Match', value: selected.submission.face_match ? `${selected.submission.face_match.toFixed(0)}%` : '—' },
                    { label: 'Liveness', value: selected.submission.liveness_score ? `${selected.submission.liveness_score.toFixed(0)}%` : '—' },
                  ].map((s, i) => (
                    <div key={i} style={{ background: '#f9f6f1', borderRadius: 8, padding: '10px 8px', textAlign: 'center' }}>
                      <div style={{ fontFamily: 'Georgia,serif', fontSize: 18, marginBottom: 2 }}>{s.value}</div>
                      <div style={{ fontSize: 10, color: '#7a7060' }}>{s.label}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Flags */}
              {selected.submission?.flags?.length > 0 && (
                <div style={{ background: '#fef3e2', borderRadius: 8, padding: '10px 12px', marginBottom: 16, fontSize: 12, color: '#b45309' }}>
                  ⚠ {selected.submission.flags.join(', ')}
                </div>
              )}

              {/* User info */}
              <div style={{ marginBottom: 16 }}>
                {[
                  ['Nombre', selected.full_name || '—'],
                  ['Email', selected.email],
                  ['País', selected.country || '—'],
                  ['Enviado', selected.submission?.submitted_at ? new Date(selected.submission.submitted_at).toLocaleString('es-ES') : '—'],
                ].map(([k, v]) => (
                  <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid #f0ebe2', fontSize: 13 }}>
                    <span style={{ color: '#7a7060' }}>{k}</span>
                    <span style={{ fontWeight: 600 }}>{v}</span>
                  </div>
                ))}
              </div>

              {/* Actions */}
              <button onClick={() => approveKyc(selected.id)} disabled={processing} style={{ width: '100%', padding: '12px', borderRadius: 8, border: 'none', background: processing ? '#e2dbd0' : '#2d6a4f', color: '#fff', fontSize: 14, fontWeight: 600, cursor: processing ? 'not-allowed' : 'pointer', marginBottom: 10 }}>
                {processing ? 'Procesando...' : '✓ Aprobar KYC'}
              </button>

              <label style={{ display: 'block', fontSize: 10.5, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: '#7a7060', marginBottom: 6 }}>Motivo de rechazo</label>
              <textarea
                style={{ width: '100%', padding: '10px', border: '1px solid #e2dbd0', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box', resize: 'vertical', minHeight: 60, marginBottom: 8 }}
                placeholder="Documento no válido, foto borrosa..."
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
              />
              <button onClick={() => rejectKyc(selected.id)} disabled={processing || !rejectReason.trim()} style={{ width: '100%', padding: '12px', borderRadius: 8, border: 'none', background: (processing || !rejectReason.trim()) ? '#e2dbd0' : '#9b2c5e', color: '#fff', fontSize: 14, fontWeight: 600, cursor: (processing || !rejectReason.trim()) ? 'not-allowed' : 'pointer' }}>
                ✕ Rechazar KYC
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
